import type { Coding } from '@medplum/fhirtypes'
import { LOINC_CODE_SYSTEM } from '#src/codeSystems.ts'

export interface GeneralObservation {
  label: string
  // Equivalent codes, primary first; the item shows whichever was recorded
  // most recently.
  codings: Coding[]
}

const loinc = (code: string, display: string): Coding => ({
  system: LOINC_CODE_SYSTEM,
  code,
  display,
})

// The fixed rows every participant's facesheet Observations card shows, in
// order, with the labels from the Facesheet Improvements design. Codes are the
// ones prod Observations actually use.
export const GENERAL_OBSERVATIONS: readonly GeneralObservation[] = [
  { label: 'Pain severity', codings: [loinc('38208-5', 'Pain severity')] },
  { label: 'Wt', codings: [loinc('29463-7', 'Body weight')] },
  { label: 'BMI', codings: [loinc('39156-5', 'Body mass index (BMI)')] },
  {
    label: 'BP Sys / BP Dias',
    codings: [
      loinc('85354-9', 'Blood pressure panel with all children optional'),
    ],
  },
  { label: 'HR', codings: [loinc('8867-4', 'Heart rate')] },
  { label: 'RR', codings: [loinc('9279-1', 'Respiratory rate')] },
  {
    label: 'O2',
    codings: [
      loinc('20564-1', 'Oxygen saturation in Blood'),
      loinc('59408-5', 'Oxygen saturation by Pulse oximetry'),
    ],
  },
  { label: 'Temp', codings: [loinc('8310-5', 'Body temperature')] },
  {
    label: 'A1c',
    codings: [loinc('4548-4', 'Hemoglobin A1c/Hemoglobin.total in Blood')],
  },
  {
    label: 'GFR',
    codings: [
      loinc('98979-8', 'eGFR (CKD-EPI 2021)'),
      loinc('50210-4', 'eGFR (cystatin C)'),
    ],
  },
]

const codingKey = (coding: Coding): string => `${coding.system}|${coding.code}`

const GENERAL_OBSERVATION_KEYS = new Set(
  GENERAL_OBSERVATIONS.flatMap((item) => item.codings.map(codingKey))
)

export function isGeneralObservationCoding(coding: Coding): boolean {
  return (
    !!coding.system &&
    !!coding.code &&
    GENERAL_OBSERVATION_KEYS.has(codingKey(coding))
  )
}

// The coding on a recorded observation that placed it in this item, so its
// history chart searches the code it was actually recorded under.
export function generalObservationCoding(
  item: GeneralObservation,
  observationCodings: Coding[] | undefined
): Coding {
  const itemKeys = new Set(item.codings.map(codingKey))
  const match = observationCodings?.find((coding) =>
    itemKeys.has(codingKey(coding))
  )
  return { ...(match ?? item.codings[0]), display: item.label }
}
